import React from 'react';
import type { FC, ComponentClass } from 'react';
import { useContext, useEffect, useState } from 'react';
import memoize from 'lodash/memoize';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { getViewMediaClientConfig } from '@confluence/fabric-media-support';
import { ExternalShareContext } from '@confluence/external-share-context';

// Types
type contentId = string;
export type InjectedProps = {
	mediaClientConfig: MediaClientConfig;
};
export type WithMediaClientConfigProps = {
	parentId: contentId;
};

// memoized so we get it only once per page
const getViewMediaClientConfigMemoized = memoize(
	(contentId: contentId, isExternalShare: boolean) =>
		getViewMediaClientConfig({ contentId, isExternalShare }),
	(...args) => args.map((arg) => arg.toString()).join('_'),
);

export default <TOriginalProps extends {}>(
	BaseComponent:
		| ComponentClass<TOriginalProps & InjectedProps>
		| React.FunctionComponent<TOriginalProps & InjectedProps>,
): FC<TOriginalProps & WithMediaClientConfigProps> => {
	type ResultProps = TOriginalProps & WithMediaClientConfigProps;

	return function WithMediaClientConfig(props: ResultProps) {
		const { parentId } = props;
		const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);

		const [mediaClientConfig, setMediaClientConfig] = useState<MediaClientConfig | null>(null);

		useEffect(() => {
			void getViewMediaClientConfigMemoized(parentId, isExternalShare).then(setMediaClientConfig);
		}, [isExternalShare, parentId]);

		if (mediaClientConfig === null) {
			return null;
		}

		return <BaseComponent mediaClientConfig={mediaClientConfig} {...props} />;
	};
};
